import { observer } from 'mobx-react-lite'
import { match } from '@/utils/pattern-match-util'
import { ServiceTypeEmptyState, ServiceTypeList, ServiceTypeListItem } from './'
import { serviceTypeIcons } from '@/features/dashboard/service-type-icons'
import type { ServiceSelectionViewModel } from '@/features/dashboard/DashboardView/ServiceSelectionViewModel'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { useTranslate } from '@/hooks/translate'

interface ServiceTypesContainerProps {
  vm: ServiceSelectionViewModel
  onStart: (type: LegendServiceType) => unknown
}

/**
 * The ServiceTypesContainer
 */
export const ServiceTypesContainer = observer(
  ({ vm, onStart }: ServiceTypesContainerProps) => {
    const T = useTranslate()
    const startButtonLabel = T('Web.Dashboard.StartNowButton', 'Start now')

    if (vm.availableServiceTypes.length === 0) {
      return <ServiceTypeEmptyState />
    }

    const isDisabled = vm.isPending
    return (
      <ServiceTypeList>
        {vm.availableServiceTypes.map((type) => {
          const isPending =
            vm.selectedServiceType === type && vm.createDraft.pending
          return match(type, {
            Tax: (
              <ServiceTypeListItem
                key={type}
                icon={serviceTypeIcons.Tax}
                title={T('Web.ServiceTypes.Tax.Title', 'Tax filing')}
                description={T(
                  'Web.ServiceTypes.Tax.Description',
                  'File your personal or business taxes with expert support.',
                )}
                isDisabled={isDisabled}
                onStart={() => onStart(type)}
                isPending={isPending}
                testId="service-types-tax-filing-item"
                startButtonLabel={startButtonLabel}
              />
            ),
            Consultation: (
              <ServiceTypeListItem
                key={type}
                icon={serviceTypeIcons.Consultation}
                title={T('Web.ServiceTypes.Consultation.Title', 'Advisory')}
                description={T(
                  'Web.ServiceTypes.Consultation.Description',
                  'Schedule a call and get your questions answered from a Pro.',
                )}
                isDisabled={isDisabled}
                isPending={isPending}
                onStart={() => onStart(type)}
                testId="service-types-consultation-item"
                startButtonLabel={startButtonLabel}
              />
            ),
            Bookkeeping: (
              <ServiceTypeListItem
                key={type}
                icon={serviceTypeIcons.Bookkeeping}
                title={T('Web.ServiceTypes.Bookkeeping.Title', 'Bookkeeping')}
                description={T(
                  'Web.ServiceTypes.Bookkeeping.Description',
                  'Keep your finances organized and your books up-to-date.',
                )}
                isDisabled={isDisabled}
                isPending={isPending}
                onStart={() => onStart(type)}
                testId="service-types-bookkeeping-item"
                startButtonLabel={startButtonLabel}
              />
            ),
            Misc: (
              <ServiceTypeListItem
                key={type}
                icon={serviceTypeIcons.Misc}
                title={T('Web.ServiceTypes.Misc.Title', 'Other services')}
                description={T(
                  'Web.ServiceTypes.Misc.Description',
                  'Explore additional services tailored to your needs.',
                )}
                isDisabled={isDisabled}
                isPending={isPending}
                onStart={() => onStart(type)}
                testId="service-types-misc-item"
                startButtonLabel={startButtonLabel}
              />
            ),
          })
        })}
      </ServiceTypeList>
    )
  },
)
